import { useCallback, useState } from 'react';

import {
  parseStructuredOutputSchema,
  serializeStructuredOutputSchema,
  StructuredOutputSchemaNode,
} from './schemaEditor';

export type StructuredOutputSchemaMode = 'visual' | 'json';

type StructuredOutputSchemaDraftState = {
  schemaNode: StructuredOutputSchemaNode | null;
  rawJson: string;
  lastValidJson: string;
  schemaError: string | null;
  mode: StructuredOutputSchemaMode;
  isJsonLocked: boolean;
};

const stringifySchemaNode = (schemaNode: StructuredOutputSchemaNode) =>
  JSON.stringify(serializeStructuredOutputSchema(schemaNode), null, 2);

const getInitialState = (): StructuredOutputSchemaDraftState => ({
  schemaNode: null,
  rawJson: '',
  lastValidJson: '',
  schemaError: null,
  mode: 'visual',
  isJsonLocked: false,
});

export const useStructuredOutputSchemaDraft = () => {
  const [state, setState] = useState<StructuredOutputSchemaDraftState>(getInitialState);

  const reset = useCallback(() => {
    setState(getInitialState());
  }, []);

  const initializeFromSchemaString = useCallback((schema: string) => {
    const parsed = parseStructuredOutputSchema(schema);

    if (!parsed.ok) {
      setState({
        schemaNode: null,
        rawJson: schema,
        lastValidJson: '',
        schemaError: parsed.error,
        mode: 'json',
        isJsonLocked: true,
      });
      return;
    }

    const canonicalJson = stringifySchemaNode(parsed.value);
    setState({
      schemaNode: parsed.value,
      rawJson: canonicalJson,
      lastValidJson: canonicalJson,
      schemaError: null,
      mode: 'visual',
      isJsonLocked: false,
    });
  }, []);

  const initializeWithNode = useCallback((schemaNode: StructuredOutputSchemaNode) => {
    const canonicalJson = stringifySchemaNode(schemaNode);
    setState({
      schemaNode,
      rawJson: canonicalJson,
      lastValidJson: canonicalJson,
      schemaError: null,
      mode: 'visual',
      isJsonLocked: false,
    });
  }, []);

  const handleVisualChange = useCallback((schemaNode: StructuredOutputSchemaNode) => {
    const canonicalJson = stringifySchemaNode(schemaNode);
    setState((currentState) => ({
      ...currentState,
      schemaNode,
      rawJson: canonicalJson,
      lastValidJson: canonicalJson,
      schemaError: null,
    }));
  }, []);

  const handleJsonChange = useCallback((rawJson: string) => {
    setState((currentState) => {
      const parsed = parseStructuredOutputSchema(rawJson);

      if (!parsed.ok) {
        return {
          ...currentState,
          rawJson,
          schemaError: parsed.error,
        };
      }

      return {
        ...currentState,
        schemaNode: parsed.value,
        rawJson,
        lastValidJson: stringifySchemaNode(parsed.value),
        schemaError: null,
        isJsonLocked: false,
      };
    });
  }, []);

  const openJsonMode = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      mode: 'json',
    }));
  }, []);

  const openVisualMode = useCallback(() => {
    setState((currentState) => {
      if (currentState.isJsonLocked || currentState.schemaError || !currentState.schemaNode) {
        return currentState;
      }

      return {
        ...currentState,
        mode: 'visual',
        rawJson: stringifySchemaNode(currentState.schemaNode),
      };
    });
  }, []);

  const revertToLastValidJson = useCallback(() => {
    setState((currentState) => {
      if (!currentState.lastValidJson) {
        return currentState;
      }

      const parsed = parseStructuredOutputSchema(currentState.lastValidJson);

      if (!parsed.ok) {
        return currentState;
      }

      return {
        ...currentState,
        schemaNode: parsed.value,
        rawJson: currentState.lastValidJson,
        schemaError: null,
        isJsonLocked: false,
      };
    });
  }, []);

  const formatJson = useCallback(() => {
    setState((currentState) => {
      const parsed = parseStructuredOutputSchema(currentState.rawJson);

      if (!parsed.ok) {
        return {
          ...currentState,
          schemaError: parsed.error,
        };
      }

      const canonicalJson = stringifySchemaNode(parsed.value);

      return {
        ...currentState,
        schemaNode: parsed.value,
        rawJson: canonicalJson,
        lastValidJson: canonicalJson,
        schemaError: null,
        isJsonLocked: false,
      };
    });
  }, []);

  const canRecoverJson = Boolean(state.schemaError) && state.rawJson !== state.lastValidJson;
  const canSwitchToVisual = !state.isJsonLocked && !state.schemaError && Boolean(state.schemaNode);

  return {
    schemaNode: state.schemaNode,
    rawJson: state.rawJson,
    lastValidJson: state.lastValidJson,
    schemaError: state.schemaError,
    mode: state.mode,
    isJsonLocked: state.isJsonLocked,
    canRecoverJson,
    canSwitchToVisual,
    reset,
    initializeFromSchemaString,
    initializeWithNode,
    handleVisualChange,
    handleJsonChange,
    openJsonMode,
    openVisualMode,
    revertToLastValidJson,
    formatJson,
  };
};
